import { subHours, subDays, subWeeks, subMonths, formatISO } from 'date-fns';

export const promptNames = ["WordPress", "PUBLISHER_1", "PUBLISHER_2", "PUBLISHER_3"];

// export const promptNamesHidden = ["WordPress", "Publisher1"];

export const normalThemes = ['none','technology','science','streaming'];
export const makeLabel = (item) => {
    //console.log('makeLabel',item);
    if (item.keyword) {
        return `${item.keyword} - (in ${item.keywordLoc ?? "all fields"})`
    } else {
        return `${item.label?.eng} - (${item?.type})`
    }
}

function buildDynamicNewsApiQuery(words, hebrews, arabics, dateRange) {
    // Helper function to format keywords or concepts for search
    // Assuming the existence of this function from the initial code

    // Construct the $and condition for the original words

    const andConditionsForWords = words.map(word => formatKeywordOrConceptForSearch(word));

    // Construct the $and condition for the translations
    const hebrewCond = hebrews.map(heb => formatKeywordOrConceptForSearch(heb));
    const arabicCond = arabics.map(arab => formatKeywordOrConceptForSearch(arab));


    // Initialize the query with the date range conditions
    const query = {
        '$and': [
            {dateStart: formatISO(dateRange.dateStart, { representation: 'date' })},
            {dateEnd: formatISO(dateRange.dateEnd, { representation: 'date' })},
        ]
    };

    // Combine language conditions in an $or condition if they are not empty
    let languageConditions = [];
    if (andConditionsForWords.length > 0) languageConditions.push({ "$and": andConditionsForWords });
    if (hebrewCond.length > 0) languageConditions.push({ "$and": hebrewCond });
    if (arabicCond.length > 0) languageConditions.push({ "$and": arabicCond });

    // Only add the language conditions if any of them are not empty
    if (languageConditions.length > 0) {
        query['$and'].push({ "$or": languageConditions });
    }

    return query;
}

// Every catalogued language, from the shared registry. This was an 18-name
// literal while the corpus carried 61 languages, so 15.4% of articles —
// Korean, Indonesian, Vietnamese, Japanese and 37 others — could not be
// filtered for at all. The first 18 keep their historical order.
export { LANGUAGE_NAMES as languages } from '../../utils/languages';

// Categories are now fetched dynamically from the API via getCategories()
// This includes virtual categories (Sport IL, Lifestyle, etc.) from the database

// Re-exported from the shared registry (src/utils/languages.js), which mirrors
// backend/utils/languages.js. The ~150-entry table that used to live here had
// drifted: it knew "fas" and "fa" for Persian but not "per", so a Persian
// article tagged with the ISO 639-2/B code rendered as the literal string "per".
export { getLanguageName } from '../../utils/languages';





export const formatDate = (date,withoutSeconds = false) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US',
        {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: !withoutSeconds?'2-digit':undefined
        }).toLowerCase();
};


export function extractKeywords(keywords) {
    const result = [];
    const seen = new Set();

    const normalize = (kw) => {
        if (!kw) return null;
        const trimmed = kw.trim();
        const needsQuotes = /\s/.test(trimmed) && !/^".*"$/.test(trimmed);
        const quoted = needsQuotes ? `"${trimmed}"` : trimmed;
        const key = quoted.toLowerCase();
        if (!seen.has(key)) {
            seen.add(key);
            result.push(quoted);
        }
    };

    const extractFromArray = (array) => {
        array.forEach(entry => {
            if (entry.value && entry.value.keyword) {
                normalize(entry.value.keyword);
            }
        });
    };

    if (keywords.$or) extractFromArray(keywords.$or);
    if (keywords.$and) extractFromArray(keywords.$and);
    if (keywords.$not) extractFromArray(keywords.$not);
    if (keywords.$english) extractFromArray(keywords.$english);
    if (keywords.$hebrew) extractFromArray(keywords.$hebrew);
    if (keywords.$arabic) extractFromArray(keywords.$arabic);

    return result;
}

export function getSearchTitle(keywords_, selectedCategories, selectedTimeRange, languages = null, html = false) {
    let title = '';

    // Handle selectedCategories as array or string
    const categoriesArray = Array.isArray(selectedCategories) ? selectedCategories : [selectedCategories];
    const hasCategories = categoriesArray.length > 0 && categoriesArray[0] !== 'none' && categoriesArray[0] !== 'All categories';
    const categoriesText = hasCategories ? categoriesArray.join(', ') : 'news';
    const categoriesLower = hasCategories ? categoriesArray.map(c => c.toLowerCase()).join(', ') : 'none';

    // For plain text
    const plainKeywords = keywords_ && keywords_.length > 0 ? ` for ${keywords_.join(', ')}` : '';
    const plainCategories = hasCategories ? categoriesText : 'news';
    const plainTimeRange = `${selectedTimeRange}`;
    const plainLanguages = languages ? ` in ${languages}` : '';

    selectedTimeRange  = selectedTimeRange.toLowerCase();
    languages = languages === 'All languages'  ? languages.toLowerCase():languages;


    // For HTML formatted text
    const styledKeywords = keywords_ && keywords_.length > 0
        ? ` for <span style="color: #FF6B35; font-weight: bold;">${keywords_.join(', ')}</span>`
        : '';

    const styledCategories = hasCategories
        ? `<span style="color: #FF6B35; font-weight: bold;">${categoriesText}</span>`
        : 'news';

    const styledTimeRange = `<span style="color: #FF6B35; font-weight: bold;">${selectedTimeRange}</span>`;

    const languagesStr = typeof languages === "string" ? languages : languages.join(', ') + ".";
    const styledLanguages = languages
        ? ` in <span style="color: #FF6B35; font-weight: bold;">${languagesStr}</span>`
        : '';
    // Construct the title based on categories, time range, and keywords
    if (!hasCategories) {
        if (html) {
            title = selectedTimeRange !== "live"
                ? `Showing trending news from the ${styledTimeRange}`
                : `Showing trending ${styledTimeRange} news`;
            title += styledKeywords;
        } else {
            title = selectedTimeRange !== "live"
                ? `Showing trending news from the ${plainTimeRange}`
                : `Showing trending ${plainTimeRange} Showing news`;
            title += plainKeywords;
        }
    } else {
        if (html) {
            title = selectedTimeRange !== "live"
                ? `Showing trending ${styledCategories} news from the ${styledTimeRange}`
                : `Showing trending ${styledTimeRange} ${styledCategories} news`;
            title += styledKeywords;
        } else {
            title = selectedTimeRange !== "live"
                ? `Showing trending ${plainCategories} news from the ${plainTimeRange}`
                : `Showing trending ${plainTimeRange} ${plainCategories} news`;
            title += plainKeywords;
        }
    }

    // Append language if present
    if (html) {
        title += styledLanguages;
    } else {
        title += plainLanguages;
    }

    return title;
}

const wiredSourceTitles = ['News','Home','HOME','Sport','Football','Apple','ספורט','ראשי']
export function getSourceTitle(source) {
    let title
    if (wiredSourceTitles.includes(source?.title)) {
        if (source.uri) {
            title = source.uri;
        }
    }
    if (!title) {
        title = source?.title || (typeof source === 'string' ? source : 'unknown')
    }

    if (title.includes('ETtoday')) {
        title = 'ET Today';
    }
    if (title.includes('������.Ru')){
        title = 'Gazeta.Ru';
    }
   // console.log('getSourceTitle',source)
    return title.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '');
}

/**
 * The publisher name to show for an article, on every screen.
 *
 * `publisherName` is resolved server-side from the Publisher directory (see
 * backend/utils/publisherDisplayName.js) and is the same answer the preferred
 * publisher star is keyed on.
 *
 * ORDER MATTERS, and the old order was the bug. Screens used to read
 * `sources[0]` first, but on the main feed path `sources` is NOT "this
 * article's sources": it starts empty and `handleDuplicatedArt` appends the
 * OTHER publishers that ran the same body. On a nypost.com article it held
 * [Pulptastic, UNILAD, Mail Online], so `sources[0]` named a syndicated copy
 * and the bucket screen called the New York Post "Pulptastic". `article.source`
 * is the article's own publisher, so it comes first, and `sources[0]` is a last
 * resort for a document that somehow carries no `source` at all.
 *
 * Every surface that names an article's publisher calls THIS, so the bucket
 * screen and the Articles step cannot drift apart again.
 */
export function getArticlePublisherName(article) {
    if (!article) return 'Unknown Source';
    if (article.publisherName) return article.publisherName;
    if (article.source) return getSourceTitle(article.source);
    if (article.sources && article.sources[0]) return getSourceTitle2(article.sources[0]);
    return 'Unknown Source';
}

export function getSourceTitle2(source) {
    // Return early if source is null or undefined
    if (!source) {
        console.warn('getSourceTitle2 called with null or undefined source');
        return 'Unknown Source';
    }
    
    let title;
    
    // Check if source has a title property, if not use uri or a fallback
    if (!source.title) {
        title = source.uri || 'Unknown Source';
    } else {
        title = !wiredSourceTitles.includes(source.title) ? source.title : (source.uri || source.title);
    }
    
    // Handle special cases
    if (title && title.includes('ETtoday')) {
        title = 'ET Today';
    }
    if (title && title.includes('.Ru')){
        title = 'Gazeta.Ru';
    }
    
    // Clean up the title if it's a string, otherwise return a fallback
    return typeof title === 'string' ? 
        title.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '') : 
        'Unknown Source';
}


export function convertTimeRangeToDateRange(timeRange) {
    const now = new Date();
    let startDate;

    switch(timeRange) {
        case 'Last Hour':
            startDate = subHours(now, 1);
            break;
        case 'Last 2 Hours':
            startDate = subHours(now, 2);
            break;
        case 'Last 4 Hours':
            startDate = subHours(now, 4);
            break;
        case 'Last 12 Hours':
            startDate = subHours(now, 12);
            break;
        case 'Last 24 Hours':
        case 'Live':
            startDate = subHours(now, 24);
            break;
        case 'Last 2 Days':
            startDate = subHours(now, 48);
            break;
        case 'Last 3 Days':
            startDate = subHours(now, 72);
            break;
        case 'Last Week':
            startDate = subHours(now, 168);
            break;
        case 'Last Month':
            startDate = subHours(now, 720);
            break;
            //data: [ '66e14d610d80f9b1a5398be5', '66d6ef29f84850b2334b6da3' ],

        default:
            startDate = now;
            break;
    }

    return {
        dateStart: formatISO(startDate, { representation: 'basic' }),
        dateEnd: formatISO(now, { representation: 'basic' })
    };
}

export function countOf(str, searchValue) {
    // Use a regular expression with the global flag to match all occurrences
    const matches = str.match(new RegExp(searchValue, 'g'));

    // If matches is null, return 0, otherwise return the length of the matches array
    return matches ? matches.length : 0;
}

export function timeAgo(inputDate) {
    const now = new Date();
    const past = new Date(inputDate);

    const differenceInMs = now - past;

    // Convert milliseconds to minutes, hours, and days
    const minutes = Math.floor(differenceInMs / (1000 * 60));
    const hours = Math.floor(differenceInMs / (1000 * 60 * 60));
    const days = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
        return minutes === 1 ? `${minutes} minute ago` : `${minutes} minutes ago`;
    } else if (hours < 24) {
        return hours === 1 ? `${hours} hour ago` : `${hours} hours ago`;
    } else {
        return days === 1 ? `${days} day ago` : `${days} days ago`;
    }
}


export const formatQuery = (keywords, sources, dateRange) => {
    return buildDynamicNewsApiQuery(keywords['$and'],
        keywords['$hebrew'],
        keywords['$arabic'],
        dateRange
        );
}

const formatKeywordOrConceptForSearch = (word) => {
    const formattedWord = {};
    if (word.value.keyword) {
        let kw = word.value.keyword;
        const trimmed = (kw || '').trim();
        const needsQuotes = /\s/.test(trimmed) && !/^".*"$/.test(trimmed);
        formattedWord.keyword = needsQuotes ? `"${trimmed}"` : trimmed;
        if (word.value.keywordLoc) { // i.e. body or title
            formattedWord.keywordLoc = word.value.keywordLoc;
        }
    }
    if (word.value.uri) {
        formattedWord.conceptUri = word.value.uri;
    }
    return formattedWord;
}

export function extractRootDomain(url) {
   // console.log('extractRootDomain',url)
    let hostname = new URL(url).hostname; // Extract hostname from URL
    hostname = hostname.replace(/^www\d*\./, ''); // Remove 'www', 'www1', 'www2', etc.

    if (hostname === "folha.uol.com.br") {
        return hostname;
    }

    if (hostname === "english.alarabiya.net") {
        return "alarabiya.net";
    }

    if (hostname === "english.almayadeen.net") {
        return "almayadeen.net";
    }

    if (hostname === "bbc.co.uk") {
        return "bbc.com";
    }

    if (hostname === "edition.cnn.com") {
        return "cnn.com";
    }

    const hostnameParts = hostname.split('.'); // Split hostname into parts

    // Handling domains with ccTLDs that usually have a second-level domain (SLD)
    // e.g., "example.co.uk" should return "co.uk"
    const ccTLDs = ['com.sa','com.tw','com.au','com.cn','com.br','org.il','or.jp','co.jp','org.eg','co.za','co.uk', 'org.uk',
        'gov.uk', 'ac.uk', 'me.uk', 'ltd.uk', 'plc.uk', 'net.uk','net.au',
        'sch.uk', 'judiciary.uk', 'parliament.uk', 'mod.uk', 'nhs.uk',
        'police.uk', 'gov.scot', 'gov.wales', 'gov.uk','co.il']; // Extend this list as needed

    // Check for known ccTLD patterns and return the appropriate root domain
    for (let i = 0; i < ccTLDs.length; i++) {
        if (hostname.endsWith(ccTLDs[i])) {
            return hostnameParts.slice(-3).join('.'); // Returns the last three parts for ccTLDs, e.g., "example.co.uk"
        }
    }

    if (hostnameParts.length > 2 && !hostnameParts[hostnameParts.length - 2].match(/^(co|com|net|org|gov|edu)$/)) {
        return hostnameParts.slice(-3).join('.'); // Return the last three parts for deeper subdomains
    } else {
        return hostnameParts.slice(-2).join('.'); // Default case for TLDs and most other domains
    }
}